import * as Service from 'types/service';
import { Features } from '@tock/coretypes';
import { ServiceDefinedState } from 'state/service/config';
import { StoreType } from '@tock/shared/utils/connect';
import { concat, find, map, sortBy } from 'es-toolkit/compat';
import { createSecondaryStates, filterPartyStates } from 'utils/businessDefinedStateUtils';
import { createSelector } from 'reselect';
import {
  getDefaultEmotionDisplayInformation,
  getDisplayInfoForPartyState,
  getStateName,
  getVariableColorFromBusinessStringColor,
} from 'utils/partyUtils';
import PartyStates from 'constants/partyStateConstants';

export const secondaryStatesSelector = createSelector(
  (state: StoreType) => state.service.config.businessDefinedStates,
  (state: StoreType) => state.features.features,
  (businessDefinedStates: ServiceDefinedState[], features: Features): Service.StateGroupingMap => {
    const partyStatusRefreshEnabled = features['PARTY_STATUS_REFRESH'] || false;
    const secondaryStates: Service.StateDescription[] = createSecondaryStates(
      businessDefinedStates,
      partyStatusRefreshEnabled
    );
    const partyStateEnums = Object.values(PartyStates);
    const primaryStates: Service.StateDescription[] = map(partyStateEnums, (primaryState) => {
      const customizationOfState = find(
        businessDefinedStates,
        (stateConfig) => primaryState === stateConfig.primaryState && stateConfig.stateName == null
      );
      const displayInfo = getDisplayInfoForPartyState(
        primaryState,
        partyStatusRefreshEnabled,
        customizationOfState
      );
      return {
        ...displayInfo,
        primaryState,
        secondaryValue: null,
      };
    });
    const split = filterPartyStates(concat(primaryStates, secondaryStates));
    return {
      EXPECTED: sortBy(split.expected, 'ordering'),
      SEATED: sortBy(split.seated, 'ordering'),
      NO_SHOW: sortBy(split.noshow, 'ordering'),
      LEFT: sortBy(split.left, 'ordering'),
      CANCELED: sortBy(split.canceled, 'ordering'),
    };
  }
);

export const emotionPartyStatesSelector = createSelector(
  (state: StoreType) => state.service.config.businessDefinedStates,
  (businessDefinedStates: ServiceDefinedState[]): ServiceDefinedState[] => {
    const partyStateEnums = Object.values(PartyStates);

    const missingPartyStates = partyStateEnums.filter(
      (partyPrimaryState) =>
        !businessDefinedStates.find(
          (definedPartyState) => partyPrimaryState === definedPartyState.primaryState
        )
    );

    return [
      ...businessDefinedStates.map((businessDefinedState) => {
        return {
          ...businessDefinedState,
          color: getVariableColorFromBusinessStringColor(businessDefinedState.color),
          stateName:
            businessDefinedState.stateName ?? getStateName(businessDefinedState.primaryState),
        };
      }),
      ...missingPartyStates.map((partyState) => getDefaultEmotionDisplayInformation(partyState)),
    ];
  }
);
